import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  present: "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8",
  absent: "border-[#FF3636]/30 text-[#DC2626] bg-[#FF3636]/8",
  late: "border-[#F59E0B]/30 text-[#D97706] bg-[#F59E0B]/8",
};

const SUBJECTS = ["Data Structures", "Artificial Intelligence", "Database Management Systems", "Operating Systems", "Computer Networks"];

export default function Attendance() {
  const [data, setData] = useState([]);
  const [report, setReport] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("records");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [markForm, setMarkForm] = useState({ student_name: "", student_email: "", subject: SUBJECTS[0], date: new Date().toISOString().split("T")[0], status: "present", class_code: "DS301" });
  const [marking, setMarking] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [d, s, r] = await Promise.all([
        axios.get(`${API}/attendance`, { withCredentials: true }),
        axios.get(`${API}/attendance/stats`, { withCredentials: true }),
        axios.get(`${API}/attendance/report`, { withCredentials: true }),
      ]);
      setData(d.data); setStats(s.data); setReport(r.data);
    } catch {} finally { setLoading(false); }
  };

  const handleMark = async (e) => {
    e.preventDefault(); setMarking(true);
    try {
      await axios.post(`${API}/attendance`, markForm, { withCredentials: true });
      fetchAll();
    } catch {} finally { setMarking(false); }
  };

  const filtered = subjectFilter ? data.filter(d => d.subject === subjectFilter) : data;

  return (
    <div className="space-y-6" data-testid="attendance-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Attendance Management</h2>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Records", value: stats.total || 0, color: "#0A7AFF" },
          { label: "Present", value: stats.present || 0, color: "#22C55E" },
          { label: "Absent", value: stats.absent || 0, color: "#FF3636" },
          { label: "Avg Rate", value: `${stats.rate || 0}%`, color: "#8B5CF6" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#D8D4CB] pb-0">
        {["records", "report", "mark"].map(t => (
          <button key={t} onClick={() => setActiveTab(t)} data-testid={`tab-${t}`}
            className={`font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-5 py-3 border-b-2 transition-all ${activeTab === t ? "border-[#0C0C10] text-[#0C0C10]" : "border-transparent text-[#BDBAA8] hover:text-[#7A7468]"}`}>
            {t === "mark" ? "Mark Attendance" : t}
          </button>
        ))}
      </div>

      {activeTab === "records" && (
        <>
          <div className="flex gap-2 flex-wrap items-center">
            <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}
              className="font-['DM_Sans'] text-xs bg-white border border-[#D8D4CB] px-3 py-2 text-[#0C0C10] focus:outline-none focus:border-[#0C0C10]">
              <option value="">All Subjects</option>
              {SUBJECTS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="bg-white border border-[#E2DDD4]">
            <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1.5fr 100px 100px 80px" }}>
              {["Student", "Subject", "Date", "Class", "Status"].map(h => (
                <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
              ))}
            </div>
            {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
              : filtered.slice(0, 50).map((item, i) => (
              <div key={i} className="grid gap-0 px-5 py-3 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1.5fr 100px 100px 80px" }}>
                <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{item.student_name}</span>
                <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.subject}</span>
                <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{item.date}</span>
                <span className="font-['Syne'] text-[10px] font-bold text-[#BDBAA8] uppercase">{item.class_code}</span>
                <span className={`font-['Syne'] font-bold text-[9px] uppercase tracking-wide px-2 py-1 border w-fit ${STATUS_STYLES[item.status]}`}>{item.status}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === "report" && (
        <div className="bg-white border border-[#E2DDD4]">
          <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "2fr 80px 80px 100px" }}>
            {["Subject", "Total", "Present", "Rate"].map(h => <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>)}
          </div>
          {report.map((r, i) => (
            <div key={i} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 items-center" style={{ gridTemplateColumns: "2fr 80px 80px 100px" }}>
              <span className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{r.subject}</span>
              <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10]">{r.total}</span>
              <span className="font-['JetBrains_Mono'] text-xs text-[#22C55E]">{r.present}</span>
              <div>
                <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{r.rate}%</span>
                <div className="w-20 h-1 bg-[#F0ECE4] mt-1"><div className="h-1 bg-[#22C55E]" style={{ width: `${r.rate}%` }} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "mark" && (
        <div className="bg-white border border-[#E2DDD4] p-6 max-w-lg">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-5">Mark Attendance</p>
          <form onSubmit={handleMark} className="space-y-5">
            {[["student_name", "Student Name"], ["student_email", "Student Email"]].map(([f, l]) => (
              <div key={f}>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">{l}</label>
                <input value={markForm[f]} onChange={e => setMarkForm(p => ({...p, [f]: e.target.value}))} required
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Subject</label>
                <select value={markForm.subject} onChange={e => setMarkForm(p => ({...p, subject: e.target.value}))}
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                  {SUBJECTS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Status</label>
                <select value={markForm.status} onChange={e => setMarkForm(p => ({...p, status: e.target.value}))}
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                  <option>present</option><option>absent</option><option>late</option>
                </select>
              </div>
            </div>
            <button type="submit" disabled={marking} className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest px-6 py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center gap-2">
              {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
